import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';

export type PresenceStatus = 'available' | 'away';
export type MayNotRespondReason = 'away' | 'inactive';

export interface MentionableUser {
    _id: string;
    displayName: string;
    position: string;
    role: string;
    email?: string;
    presenceStatus: PresenceStatus;
    statusNote?: string;
    awayStartsAt?: string;
    awayEndsAt?: string;
    lastActive?: string;
    mayNotRespond: boolean;
    mayNotRespondReason?: MayNotRespondReason;
}

const getMentionableUsers = async (sessionId: string, orderId: string): Promise<MentionableUser[]> => {
    const { data } = await axios.post(`${API_URL}/order/mentionable-users`,
        { orderId },
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export const useGetMentionableUsers = (orderId: string) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: ['/order/mentionable-users', orderId],
        queryFn: () => getMentionableUsers(sessionContext?.sessionId ?? '', orderId),
        enabled: !!sessionContext?.sessionId && !!orderId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export function formatAwayUntil(awayEndsAt?: string): string {
    if (!awayEndsAt) return '';
    const d = new Date(awayEndsAt);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

export interface MayNotRespondSummary {
    /** All currently-flagged user ids, for dismiss-all tracking. */
    userIds: string[];
    /** Single joined line for inactive users, or null if none. */
    inactiveLine: string | null;
    /** One line per away user (each has its own time/note). */
    awayLines: string[];
}

function joinNames(names: string[]): string {
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/** Build a single consolidated "may not respond" summary. */
export function buildMayNotRespondSummary(
    users: MentionableUser[] | undefined,
): MayNotRespondSummary | null {
    const flagged = (users ?? []).filter(u => u.mayNotRespond);
    if (!flagged.length) return null;

    const inactive = flagged.filter(u => u.mayNotRespondReason !== 'away');
    const away = flagged.filter(u => u.mayNotRespondReason === 'away');

    const inactiveNames = inactive.map(u => u.displayName);
    const inactiveLine = inactiveNames.length
        ? `${joinNames(inactiveNames)} ${
              inactiveNames.length === 1 ? "hasn't" : "haven't"
          } been on Yaxle in over 8 business hours and may not respond.`
        : null;

    const awayLines = away.map(u => {
        const until = formatAwayUntil(u.awayEndsAt);
        const note = u.statusNote?.trim();
        let text = until
            ? `${u.displayName} is away until ${until} and may not respond.`
            : `${u.displayName} is away and may not respond.`;
        if (note) text += ` Note: ${note}`;
        return text;
    });

    return { userIds: flagged.map(u => u._id), inactiveLine, awayLines };
}
